import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronUp, ChevronDown } from 'lucide-react';
import VenueCard from '../Venue/VenueCard';

// Calculate responsive heights
const calculateResponsiveHeights = (screenWidth, screenHeight) => {
  const isMobile = screenWidth < 768;
  const isTablet = screenWidth >= 768 && screenWidth < 1024;
  const isPortrait = screenHeight > screenWidth;
  const isLargePortrait = screenWidth >= 1024 && isPortrait;

  if (isMobile) {
    return { collapsed: 60, half: screenHeight * 0.4, full: screenHeight * 0.8 };
  } else if (isTablet || isLargePortrait) {
    return { collapsed: 60, half: screenHeight * 0.35, full: screenHeight * 0.75 };
  } else {
    return { collapsed: 60, half: screenHeight * 0.3, full: screenHeight * 0.7 };
  }
};

// Ad Placeholder Component
const AdPlaceholder = () => {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        width: '100%',
        height: '100px',
        backgroundColor: '#fef3c7',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px dashed #fbbf24',
        marginBottom: '12px'
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '24px', marginBottom: '4px' }}>📢</div>
        <div style={{ fontSize: '12px', color: '#92400e', fontWeight: '600' }}>
          {t('map.sponsoredVenue')}
        </div>
      </div>
    </motion.div>
  );
};

const VenueDrawer = ({ 
  venues = [], 
  isLoading = false, 
  userLocation = null,
  hasSearchQuery = false,
  searchQuery = '',
  totalCount = 0
}) => {
  const { t } = useTranslation();
  const drawerRef = useRef(null);
  const dragStateRef = useRef({ isDragging: false, startY: 0, startHeight: 0 });
  
  // Screen size
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth < 768
  });
  
  const responsiveHeights = useMemo(
    () => calculateResponsiveHeights(screenSize.width, screenSize.height),
    [screenSize.width, screenSize.height]
  );
  
  // Drawer height and state
  const [height, setHeight] = useState(responsiveHeights.collapsed);
  const [isDragging, setIsDragging] = useState(false);
  const [drawerState, setDrawerState] = useState('collapsed');
  
  // Update drawer state based on height
  useEffect(() => {
    const { collapsed, half, full } = responsiveHeights;
    
    if (height <= collapsed + 20) {
      setDrawerState('collapsed');
    } else if (height < full - 50) {
      setDrawerState('half');
    } else {
      setDrawerState('full');
    }
  }, [height, responsiveHeights]);
  
  // Auto-expand to half when search has results or venues load
  useEffect(() => {
    if (venues.length > 0 && height <= responsiveHeights.collapsed + 10) {
      // ✅ FIX: Expand to half when venues arrive
      setHeight(responsiveHeights.half);
    } else if (venues.length === 0 && !hasSearchQuery && height > responsiveHeights.collapsed) {
      // Collapse only if no search and no venues
      setHeight(responsiveHeights.collapsed);
    }
  }, [venues.length, hasSearchQuery, responsiveHeights.half, responsiveHeights.collapsed, height]);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
        isMobile: window.innerWidth < 768
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Touch/Mouse drag handlers
  const handleDragStart = useCallback((e) => {
    const clientY = e.type === 'mousedown' ? e.clientY : e.touches[0].clientY;
    dragStateRef.current = {
      isDragging: true,
      startY: clientY,
      startHeight: height
    };
    setIsDragging(true);
  }, [height]);
  
  const handleDragMove = useCallback((e) => {
    if (!dragStateRef.current.isDragging) return;
    
    const clientY = e.type === 'mousemove' ? e.clientY : e.touches[0].clientY;
    const deltaY = dragStateRef.current.startY - clientY;
    const newHeight = Math.max(
      responsiveHeights.collapsed,
      Math.min(responsiveHeights.full, dragStateRef.current.startHeight + deltaY)
    );
    
    setHeight(newHeight);
  }, [responsiveHeights]);
  
  const handleDragEnd = useCallback(() => {
    if (!dragStateRef.current.isDragging) return;
    
    dragStateRef.current.isDragging = false;
    setIsDragging(false);
    
    // Snap to nearest state
    const { collapsed, half, full } = responsiveHeights;
    const distToCollapsed = Math.abs(height - collapsed);
    const distToHalf = Math.abs(height - half);
    const distToFull = Math.abs(height - full);
    
    const minDist = Math.min(distToCollapsed, distToHalf, distToFull);
    
    if (minDist === distToCollapsed && venues.length === 0) {
      setHeight(collapsed);
    } else if (minDist === distToHalf || minDist === distToCollapsed) {
      setHeight(half);
    } else {
      setHeight(full);
    }
  }, [height, responsiveHeights, venues.length]);
  
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove);
      window.addEventListener('touchend', handleDragEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);
  
  // Render venue list with ad placeholders
  const venueListWithAds = useMemo(() => {
    const items = [];
    venues.forEach((venue, index) => {
      // Add ad placeholder every 5 cards
      if (index > 0 && index % 5 === 0) {
        items.push({ type: 'ad', key: `ad-${index}` });
      }
      items.push({ type: 'venue', venue, key: venue._id || venue.Dubai_id || `venue-${index}` });
    });
    return items;
  }, [venues]);
  
  return (
    <motion.div
      ref={drawerRef}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: `${height}px`,
        backgroundColor: 'white',
        borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
      animate={{ height }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
    >
      {/* Drag Handle */}
      <div
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        style={{
          padding: '12px 0',
          cursor: isDragging ? 'grabbing' : 'grab',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: 'white',
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          position: 'relative',
          zIndex: 10
        }}
      >
        {/* Handle Bar */}
        <div style={{
          width: '40px',
          height: '4px',
          backgroundColor: '#d1d5db',
          borderRadius: '9999px'
        }} />
        
        {/* State Indicator */}
        {drawerState === 'collapsed' && (
          <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>
            {hasSearchQuery ? (
              <span>{t('map.drawerResultsFor', { count: venues.length, query: searchQuery })}</span>
            ) : (
              <span>{t('map.drawerVenuesNearby', { count: venues.length })} {totalCount > venues.length && `· ${t('map.drawerTotalCount', { count: totalCount })}`}</span>
            )}
          </div>
        )}
        
        {drawerState === 'full' && (
          <ChevronDown size={20} color="#6b7280" />
        )}
        
        {drawerState === 'half' && (
          <ChevronUp size={20} color="#6b7280" />
        )}
      </div>
      
      {/* Content Area */}
      <div style={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Loading State */}
        {isLoading && (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: '#6b7280',
            fontSize: '14px'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔍</div>
            {t('map.drawerSearchingVenues')}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && venues.length === 0 && hasSearchQuery && (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: '#6b7280',
            fontSize: '14px'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🤷</div>
            {t('map.noResults')}
          </div>
        )}
        
        {/* Venue List */}
        {!isLoading && venues.length > 0 && (
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '0 16px 20px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {venueListWithAds.map((item) => {
              if (item.type === 'ad') {
                return <AdPlaceholder key={item.key} />;
              }
              return (
                <VenueCard
                  key={item.key}
                  venue={item.venue}
                  userLocation={userLocation}
                />
              );
            })}
          </div>
        )}
        
        {/* Default State */}
        {!isLoading && venues.length === 0 && !hasSearchQuery && (
          <div style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: '#6b7280'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📍</div>
            <div style={{ fontSize: '13px', fontWeight: '500' }}>
              {t('map.drawerPanOrZoom')}
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
              {totalCount > 0 && t('map.drawerVenuesLoaded', { count: totalCount })}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default VenueDrawer;
